import { Button, MenuItem, Menu, Tab, Tabs, Autocomplete, TextField } from '@mui/material'
import logo from '../logo.svg'
import './NavBar.css'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import { dataContext } from '..'
import { useContext, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'

type globalSearchType = {
  id: string
  objectType:
    | 'salesorder'
    | 'customer/invoice'
    | 'customer/payment'
    | 'customer/credit'
    | 'vendor/purchaseorder'
    | 'rma'
    | undefined
  ref_number: string
}

function NavBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useContext(dataContext)
  const controller = useRef(new AbortController())

  const [customerMenuAnchor, setCustomerMenuAnchor] = useState<null | HTMLElement>(null)
  const [vendorMenuAnchor, setVendorMenuAnchor] = useState<null | HTMLElement>(null)
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState<null | HTMLElement>(null)
  const [globalSearchResults, setGlobalSearchResults] = useState<globalSearchType[]>([])

  const formatGlobalSearchResults = (results: globalSearchType[]) => {
    return results.map(result => ({
      id: result.id,
      objectType: result.objectType,
      ref_number: result.ref_number,
      label:
        result.objectType === 'salesorder'
          ? `Sales Order: ${result.ref_number}`
          : result.objectType === 'customer/invoice'
          ? `Invoice: ${result.ref_number}`
          : result.objectType === 'customer/payment'
          ? `Payment: ${result.ref_number}`
          : result.objectType === 'customer/credit'
          ? `Customer Credit: ${result.ref_number}`
          : result.objectType === 'vendor/purchaseorder'
          ? `Purchase Order: ${result.ref_number}`
          : `RMA: ${result.ref_number}`,
    }))
  }

  return (
    <div className="nav-bar">
      <div className="nav-bar-left">
        <img src={logo} alt="logo" className="nav-bar-logo" />
        <Tabs value={location.pathname.split('/')[1].toLowerCase()} role="navigation" className="nav-bar-tabs">
          <Tab
            component={Link}
            value={'salesorder'}
            label="Sales Order"
            icon={<AttachMoneyOutlinedIcon />}
            iconPosition="start"
            to="/salesorder"
          />
          <Tab
            value={'customer'}
            label="Customer"
            icon={<PersonOutlineOutlinedIcon />}
            iconPosition="start"
            onClick={e => setCustomerMenuAnchor(e.currentTarget)}
          />
          <Tab
            value={'vendor'}
            label="Vendor"
            icon={<StoreOutlinedIcon />}
            iconPosition="start"
            onClick={e => setVendorMenuAnchor(e.currentTarget)}
          />
          <Tab
            component={Link}
            value={'rma'}
            label="RMA"
            icon={<ReplayOutlinedIcon />}
            iconPosition="start"
            to="/rma"
          />
          <Tab
            value={'reports'}
            label="Reports"
            icon={<ContentPasteOutlinedIcon />}
            iconPosition="start"
            onClick={e => setReportsMenuAnchor(e.currentTarget)}
          />
          {user.user_type === 'Administrator' && (
            <Tab
              component={Link}
              value={'administration'}
              label="Administration"
              icon={<PersonOutlineOutlinedIcon />}
              iconPosition="start"
              to="/administration"
            />
          )}
        </Tabs>

        {/* customer menu */}
        <Menu
          anchorEl={customerMenuAnchor}
          open={Boolean(customerMenuAnchor)}
          onClose={() => setCustomerMenuAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            component={Link}
            to="/customer/invoices"
            onClick={() => setCustomerMenuAnchor(null)}
            selected={location.pathname.includes('invoices')}
          >
            Invoices
          </MenuItem>
          <MenuItem
            component={Link}
            to="/customer/payments"
            onClick={() => setCustomerMenuAnchor(null)}
            selected={location.pathname.includes('payments')}
          >
            Payments
          </MenuItem>
          <MenuItem
            component={Link}
            to="/customer/credits"
            onClick={() => setCustomerMenuAnchor(null)}
            selected={location.pathname.includes('credits')}
          >
            Customer Credits
          </MenuItem>
        </Menu>

        {/* vendor menu */}
        <Menu anchorEl={vendorMenuAnchor} open={Boolean(vendorMenuAnchor)} onClose={() => setVendorMenuAnchor(null)}>
          <MenuItem component={Link} to="/vendor/purchaseorder" onClick={() => setVendorMenuAnchor(null)}>
            Purchase Orders
          </MenuItem>
        </Menu>

        {/* reports menu */}
        <Menu anchorEl={reportsMenuAnchor} open={Boolean(reportsMenuAnchor)} onClose={() => setReportsMenuAnchor(null)}>
          <MenuItem
            component={Link}
            to="/reports/cogsinvoice"
            onClick={() => setReportsMenuAnchor(null)}
            selected={location.pathname.includes('cogsinvoice')}
          >
            COGS Invoice
          </MenuItem>
        </Menu>
      </div>
      <div className="nav-bar-right">
        <Autocomplete
          fullWidth
          freeSolo
          filterOptions={x => x}
          size="small"
          noOptionsText="No results"
          options={formatGlobalSearchResults(globalSearchResults)}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.id}>
              {option.label}
            </MenuItem>
          )}
          renderInput={params => <TextField {...params} label="Search" />}
          onInputChange={(_, value) => {
            controller.current.abort()
            controller.current = new AbortController()
            if (value) {
              axios
                .get(`/globalSearch?query=${value}`, { signal: controller.current.signal })
                .then(res => {
                  setGlobalSearchResults(res.data)
                })
                .catch(err => {
                  if (err.name !== 'CanceledError') {
                    console.error(err)
                  }
                })
            } else {
              setGlobalSearchResults([])
            }
          }}
          onChange={(_, value) => {
            if (typeof value !== 'string' && value && value.objectType && value.id) {
              navigate(`/${value.objectType}/${value.id}`)
              setGlobalSearchResults([])
            }
          }}
        />
        <Button variant="text" startIcon={<PersonOutlineOutlinedIcon />} disabled>
          {user.display_name}
        </Button>
      </div>
    </div>
  )
}

export default NavBar
